import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Instagram } from "lucide-react";

const FOLLOWED_KEY = "solis_ig_followed";
const DISMISSED_KEY = "solis_ig_dismissed";
const SNOOZE_MS = 7 * 24 * 60 * 60 * 1000; // a closed popup stays closed for a week

export default function InstagramPopup() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(FOLLOWED_KEY)) return; // already following — never again
    const dismissed = Number(localStorage.getItem(DISMISSED_KEY) || 0);
    if (Date.now() - dismissed < SNOOZE_MS) return; // closed recently — don't nag every visit
    const t = setTimeout(() => setOpen(true), 1500);
    return () => clearTimeout(t);
  }, []);

  const close = () => {
    localStorage.setItem(DISMISSED_KEY, String(Date.now()));
    setOpen(false);
  };

  const follow = () => {
    localStorage.setItem(FOLLOWED_KEY, "1"); // clicked follow — never show again
    setOpen(false);
    window.open("https://instagram.com/Soliscasting", "_blank", "noopener");
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="instagram-popup"
          className="fixed inset-0 z-[90] flex items-center justify-center px-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
        >
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={close} />
          <motion.div
            className="relative panel max-w-md w-full p-10 text-center"
            initial={{ opacity: 0, y: 40, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          >
            <button
              data-testid="instagram-popup-close"
              onClick={close}
              aria-label="Close"
              className="absolute top-4 right-4 text-white/40 hover:text-white transition-colors"
            >
              <X size={18} />
            </button>
            <img
              src="/solis-instagram.jpg"
              alt="Solis Casting on Instagram"
              className="w-28 h-28 mx-auto mb-8 object-cover"
            />
            <p className="overline-label mb-4 !text-accent">Now on Instagram</p>
            <h2 className="font-display font-light text-3xl sm:text-4xl tracking-tight mb-4">
              Solis Casting is now on <span className="italic">Instagram</span>
            </h2>
            <p className="text-sm text-muted-foreground leading-relaxed mb-8">
              Fresh casting calls, new faces joining the board, and a look behind the scenes — follow <strong className="text-white">@Soliscasting</strong> and tag us on your shoots.
            </p>
            <button
              data-testid="instagram-popup-follow"
              onClick={follow}
              className="btn-primary w-full inline-flex items-center justify-center gap-2"
            >
              <Instagram size={16} /> Follow @Soliscasting
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
